<script lang="ts">
  import { onMount } from 'svelte';
  import AdminDashboard from '../components/AdminDashboard.svelte';
  import HouseQuestionnaire from '../components/HouseQuestionnaire.svelte';
  import SoldOutDemo from '../components/SoldOutDemo.svelte';
  import LandingPage from './pages/LandingPage.svelte';
  import CatalogPage from './pages/CatalogPage.svelte';
  import TicketPage from './pages/TicketPage.svelte';
  import EventPage from './pages/EventPage.svelte';
  import CommunityPage from './pages/CommunityPage.svelte';
  import CommunityManagePage from './pages/CommunityManagePage.svelte';
  import LinkFinderPage from './pages/LinkFinderPage.svelte';
  import AccountSettingsPage from './pages/AccountSettingsPage.svelte';
  import VidamorPage from './pages/VidamorPage.svelte';
  import Notice from './ui/Notice.svelte';
  import ErrorPanel from './ui/ErrorPanel.svelte';
  import LoadingBlock from './ui/LoadingBlock.svelte';
  import DiagnosticsBanner from './ui/DiagnosticsBanner.svelte';
  import NameGate from './components/NameGate.svelte';
  import { route, initRouter, gotoHome, gotoAdmin, gotoEvent, gotoSettings, gotoVidamor, clearUrlParams } from './router';
  import { session, startSession, openSignIn, endSession } from './session';
  import { redeemInvite, releaseConvexReservation, previewInvite } from '../lib/convexApi';
  import { crumb } from '../lib/telemetry';
  import { VIDAMOR } from './vidamorConfig';

  const INVITE_STORAGE_KEY = 'buckshot.pendingInvite';

  let booted = false;
  let pendingInviteCode: string | null = null;
  let inviteStatus: 'idle' | 'pending' | 'error' = 'idle';
  let inviteError: string | null = null;

  // Signed-out `?invite=` landing: preview the invite (public, no mutation) and
  // route to the event page in invited-preview — banner "{inviter} invited you"
  // + "Create your account" CTA (two-lists slice 06).
  let previewInviterName: string | null = null;
  let previewInviteKind: string | null = null;
  let previewEventId: string | null = null;

  function readStoredInvite(): string | null {
    try {
      return sessionStorage.getItem(INVITE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  function persistInvite(code: string | null) {
    try {
      if (code) {
        sessionStorage.setItem(INVITE_STORAGE_KEY, code);
      } else {
        sessionStorage.removeItem(INVITE_STORAGE_KEY);
      }
    } catch {
      /* private-mode storage failures are non-fatal */
    }
  }

  // Sticky sub-headers (catalog day groups) offset themselves by
  // --shell-header-h. The header's real height varies with viewport (touch
  // padding on mobile), so measure it and keep the token honest; the tokens.css
  // value is only the pre-mount fallback.
  function measureHeader(node: HTMLElement) {
    const set = () =>
      document.documentElement.style.setProperty(
        '--shell-header-h',
        `${Math.ceil(node.getBoundingClientRect().height)}px`
      );
    const observer = new ResizeObserver(set);
    observer.observe(node);
    set();
    // The display webfont swaps in after first paint and grows the brand line;
    // re-measure once fonts settle (the observer alone can miss the swap).
    document.fonts?.ready?.then(set).catch(() => {});
    return {
      destroy() {
        observer.disconnect();
      }
    };
  }

  onMount(async () => {
    const directives = initRouter();

    const invite = directives.inviteCode ?? readStoredInvite();
    if (invite) {
      pendingInviteCode = invite;
      persistInvite(invite);
    }

    if (directives.cancelledReservationId) {
      void releaseConvexReservation(directives.cancelledReservationId, 'user_cancelled').catch(err =>
        console.error('Failed to release reservation', err)
      );
      clearUrlParams('cancelled', 'canceled', 'reservation');
    }

    // Render immediately; auth resolves in the background. Ticket links work
    // without a session, and the UI upgrades in place once Clerk is ready.
    booted = true;
    void startSession();
  });

  $: if (
    booted &&
    pendingInviteCode &&
    $session.signedIn &&
    $session.personId &&
    inviteStatus !== 'pending'
  ) {
    void redeemPendingInvite();
  }

  // Signed-out with a pending invite: preview it once and route to the event
  // page in invited-preview. Signing in later triggers redeemPendingInvite.
  let previewedCode: string | null = null;
  $: if (
    booted &&
    pendingInviteCode &&
    $session.authLoaded &&
    !$session.signedIn &&
    previewedCode !== pendingInviteCode
  ) {
    previewedCode = pendingInviteCode;
    void loadInvitePreview(pendingInviteCode);
  }

  async function loadInvitePreview(code: string) {
    const result = await previewInvite(code);
    if (!result || !result.valid || !result.eventId) {
      previewInviterName = null;
      previewInviteKind = null;
      previewEventId = null;
      return;
    }
    previewInviterName = result.inviterName;
    previewInviteKind = result.kind;
    previewEventId = result.eventId;
    // Vidamor invites live inside the vidamor experience, never the generic
    // event page — the +1 flow stays branded end to end.
    if (result.eventId === VIDAMOR.eventId) {
      if ($route.name !== 'vidamor') gotoVidamor({ push: false });
      return;
    }
    if ($route.name !== 'event' || $route.eventId !== result.eventId) {
      gotoEvent(result.eventId, { push: false });
    }
  }

  async function redeemPendingInvite() {
    const code = pendingInviteCode;
    const personId = $session.personId;
    if (!code || !personId) return;

    inviteStatus = 'pending';
    inviteError = null;
    try {
      // Redemption grants standing, not a ticket (two-lists slice 06). On
      // success we land on the event page in the 'invited' standing — the buy
      // CTA there claims the invite at purchase. NOT gotoTicket.
      const result = await redeemInvite({ code, personId });
      if (!result?.eventId) {
        throw new Error('Invite could not be redeemed.');
      }
      pendingInviteCode = null;
      inviteStatus = 'idle';
      persistInvite(null);
      clearUrlParams('invite');
      if (result.eventId === VIDAMOR.eventId) {
        // Redeemed standing flips the live detail store; the vidamor page
        // reveals the ladder on its own.
        if ($route.name !== 'vidamor') gotoVidamor({ push: false });
      } else {
        gotoEvent(result.eventId, { push: false });
      }
    } catch (err) {
      pendingInviteCode = null;
      inviteStatus = 'error';
      persistInvite(null);
      clearUrlParams('invite');
      inviteError =
        typeof err === 'object' && err !== null && 'data' in err && typeof (err as any).data === 'string'
          ? (err as any).data
          : err instanceof Error
          ? err.message
          : 'Invite redemption failed.';
      console.error('Invite redemption failed', { invite: code, personId, error: err });
    }
  }

  function handleSignIn() {
    const url = new URL(window.location.href);
    if (pendingInviteCode) {
      url.searchParams.set('invite', pendingInviteCode);
    }
    openSignIn(url.toString());
  }

  async function handleSignOut() {
    await endSession();
    gotoHome();
  }

  $: landingMessage = pendingInviteCode && !$session.signedIn ? 'You have an invite waiting — sign in to claim it.' : null;

  // Name gate: once the session resolves a person and it lacks a confirmed
  // name, block the routed page with the one-field "what do we call you?" panel.
  // Signed-out ticket links still resolve — the gate needs a personId.
  $: showNameGate =
    $session.signedIn && $session.personId !== null && $session.nameConfirmedAt === null;

  // Diagnostics breadcrumbs (specs/observability): the route and auth state a
  // captured error happened under. Consecutive duplicates dedupe in the buffer.
  $: crumb(`route: ${$route.name}`);
  $: if ($session.authLoaded) {
    crumb($session.signedIn ? `session: signed-in person=${$session.personId}` : 'session: signed-out');
  }
</script>

{#if $route.name !== 'house' && $route.name !== 'vidamor'}
  <header class="shell-header" use:measureHeader>
    <button type="button" class="shell-header__brand" on:click={() => gotoHome()}>Gravity</button>
    <div class="shell-header__right">
      {#if $session.signedIn}
        <button type="button" class="shell-header__user" on:click={() => gotoSettings()}>
          {$session.displayName ?? $session.email ?? 'Signed in'}
          {#if $session.isAdmin}<span class="shell-header__role">· Admin</span>{/if}
        </button>
        {#if $session.isAdmin}
          {#if $route.name === 'admin'}
            <button type="button" class="ghost" on:click={() => gotoHome()}>Back to events</button>
          {:else}
            <button type="button" class="ghost" on:click={gotoAdmin}>Admin</button>
          {/if}
        {/if}
        <button type="button" class="secondary" on:click={handleSignOut}>Sign out</button>
      {:else if $session.authLoaded}
        <button type="button" class="accent" on:click={handleSignIn}>Sign in</button>
      {/if}
    </div>
  </header>
{/if}

<main class="shell-main">
  {#if !booted}
    <LoadingBlock label="Loading…" />
  {:else if showNameGate}
    <NameGate initialName={$session.clerkName ?? $session.email ?? ''} on:signout={handleSignOut} />
  {:else}
    {#if inviteStatus === 'pending'}
      <div class="shell-banner"><Notice>Claiming your invite…</Notice></div>
    {:else if inviteStatus === 'error' && inviteError}
      <div class="shell-banner">
        <ErrorPanel
          title="Your invite couldn't be claimed"
          hint="Double-check the link, or ask whoever invited you to send a fresh one."
          detail={inviteError}
        />
      </div>
    {/if}
    {#if $session.error}
      <div class="shell-banner"><Notice variant="error">{$session.error}</Notice></div>
    {/if}

    {#if $route.name === 'admin'}
      {#if $session.isAdmin}
        <AdminDashboard />
      {:else if $session.signedIn}
        <div class="shell-banner">
          <Notice variant="error">
            This account doesn't have admin access. Ask an owner to grant the admin role in Clerk.
          </Notice>
        </div>
      {:else}
        <LandingPage message="Sign in with an admin account to continue." on:signin={handleSignIn} />
      {/if}
    {:else if $route.name === 'house'}
      {#if $session.signedIn}
        <HouseQuestionnaire />
      {:else}
        <LandingPage message="Sign in to open the questionnaire." on:signin={handleSignIn} />
      {/if}
    {:else if $route.name === 'vidamor'}
      <!-- Standalone Vidamor experience: no sign-in gate — the page renders both
           signed-out (public letter + apply) and signed-in (branch) states. -->
      {#if !$session.authLoaded}
        <LoadingBlock label="Loading…" />
      {:else}
        <VidamorPage
          previewInviterName={!$session.signedIn && previewEventId === VIDAMOR.eventId
            ? previewInviterName
            : null}
          previewInviteCode={!$session.signedIn && previewEventId === VIDAMOR.eventId
            ? pendingInviteCode
            : null}
          on:createaccount={handleSignIn}
        />
      {/if}
    {:else if $route.name === 'demo'}
      <SoldOutDemo />
    {:else if $route.name === 'linkfinder'}
      <LinkFinderPage />
    {:else if $route.name === 'settings'}
      {#if !$session.authLoaded}
        <LoadingBlock label="Loading…" />
      {:else if $session.signedIn}
        <AccountSettingsPage />
      {:else}
        <LandingPage message="Sign in to open your account settings." on:signin={handleSignIn} />
      {/if}
    {:else if $route.name === 'communityManage'}
      <CommunityManagePage slug={$route.slug} />
    {:else if $route.name === 'community'}
      <CommunityPage slug={$route.slug} />
    {:else if $route.name === 'ticket'}
      {#if !$session.authLoaded}
        <LoadingBlock label="Loading…" />
      {:else if $session.signedIn}
        <TicketPage eventId={$route.eventId} code={$route.code} showSuccess={$route.success} />
      {:else}
        <LandingPage message="Sign in to open your ticket." on:signin={handleSignIn} />
      {/if}
    {:else if $route.name === 'event'}
      <!-- Public event page: renders for signed-out strangers too. Wait for
           auth so the viewer's standing is computed from a resolved session. -->
      {#if !$session.authLoaded}
        <LoadingBlock label="Loading…" />
      {:else}
        <EventPage
          eventId={$route.eventId}
          previewInviterName={!$session.signedIn && previewEventId === $route.eventId
            ? previewInviterName
            : null}
          previewInviteCode={!$session.signedIn && previewEventId === $route.eventId
            ? pendingInviteCode
            : null}
          previewInviteKind={!$session.signedIn && previewEventId === $route.eventId
            ? previewInviteKind
            : null}
          on:createaccount={handleSignIn}
        />
      {/if}
    {:else}
      {#if landingMessage}
        <div class="shell-banner">
          <Notice>
            {landingMessage}
            <button type="button" class="accent shell-banner__action" on:click={handleSignIn}>Sign in</button>
          </Notice>
        </div>
      {/if}
      <CatalogPage />
    {/if}
  {/if}
</main>

<DiagnosticsBanner />

<style>
  .shell-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    padding: var(--space-3) var(--space-5);
    border-top: 3px solid var(--color-accent);
    border-bottom: var(--border-subtle);
    background: var(--color-surface-950);
    position: sticky;
    top: 0;
    z-index: var(--z-raised);
  }

  .shell-header__brand {
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: var(--font-display);
    font-size: var(--font-size-xl);
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--color-text-primary);
    padding: var(--space-1) 0;
  }

  /* Never wraps: sticky day headers offset by --shell-header-h depend on the
     header keeping its one-line height on every viewport. */
  .shell-header__right {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: nowrap;
    justify-content: flex-end;
  }

  .shell-header__right button {
    white-space: nowrap;
  }

  /* Signed-in name doubles as the settings entry: quiet, ghost-like, not a loud
     button. The global bare-button rule paints an accent fill, so we set an
     explicit transparent background and reset the button chrome here. */
  .shell-header__user {
    border: none;
    background: transparent;
    padding: var(--space-1) 0;
    font: inherit;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: color var(--transition-fast);
  }

  .shell-header__user:hover {
    color: var(--color-text-primary);
  }

  .shell-header__role {
    color: var(--color-text-amber);
  }

  .shell-main {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    min-height: 60vh;
  }

  .shell-banner {
    width: 100%;
    max-width: var(--content-max-width);
    margin: var(--space-4) auto 0;
    padding: 0 var(--space-4);
    box-sizing: border-box;
  }

  .shell-banner__action {
    margin-left: var(--space-3);
  }

  @media (max-width: 600px) {
    /* Mobile: the header scrolls away with the page (Shotgun-style) so the
       catalog's sticky day headers own the top of the viewport. The catalog
       drops its --shell-header-h offset to 0 at this breakpoint to match.
       measureHeader stays — desktop still pins the header and needs the token. */
    .shell-header {
      position: static;
      padding: var(--space-3) var(--space-4);
    }

    .shell-header__user {
      display: none;
    }
  }
</style>
